import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { Stack } from "@mui/material";
import { type ContactAddress } from "@repo/api";
import { readEnv } from "@repo/helpers";
import { useAllowedChanges } from "@repo/logic";
import { cs, HeadingM } from "@repo/ui";
import { type FC, useState } from "react";
import AddressBox from "../../../components/AddressBox";
import Attention from "../../../components/AttentionBlock";
import { getContractTypeToTitle } from "../../../components/Contract/Utils/contractUtils";
import ChangeAddressDrawer from "../../../components/Drawer/ChangeAddressDrawer";
import { NotInMVPButton } from "../../../components/Drawer/NotInMVP";
import { Trigger } from "../../../components/Link";
import { Seo } from "../../../components/SEO";
import { dateTimeFormat } from "../../../functions/dates";
import withProtection from "../../../highOrderComponents/withProtection";
import { useContactAdresses, useContracts, useProfile } from "../../../queryHooks";

export const Title = "Moje adresy";

const ContactAddressBox: FC<{
	item: ContactAddress;
	formattedLabel: string;
	profileLoading: boolean;
	onNotInMvpClick: () => void;
}> = ({ item, formattedLabel, profileLoading, onNotInMvpClick }) => {
	const [formWasSend, setFormWasSend] = useState(false);
	const contractsResult = useContracts();
	const allowedChangesId =
		contractsResult.data?.find(
			(contract) =>
				contract.id === item.contractNumber ||
				contract.proposalNumber === item.contractNumber,
		)?.id ?? item.contractNumber;
	const {
		data: allowedChangesData,
		isSuccess: allowedChangesSuccess,
		refetch: refetchAllowedChanges,
	} = useAllowedChanges({
		id: allowedChangesId,
		options: { enabled: Boolean(allowedChangesId) },
	});
	const isDisabled = (() => {
		if (!allowedChangesSuccess) {
			return true;
		}

		return (
			formWasSend ||
			item.isContactAddressChangeInProgress ||
			allowedChangesData?.canChangeContactAddress !== true
		);
	})();

	return (
		<AddressBox
			address={item.address}
			label={formattedLabel}
			editAction={
				readEnv("GATSBY_FEATURE_IS_CONTACT_ADDRESS_CHANGE_ENABLED") === "true" ? (
					<ChangeAddressDrawer
						{...item}
						disabled={isDisabled}
						refetch={true}
						setFormWasSend={(wasSend) => {
							setFormWasSend(wasSend);
							void refetchAllowedChanges();
						}}
					/>
				) : (
					<NotInMVPButton
						text={cs.changeAddress.changeContactAddress}
						disabled={profileLoading}
						onClick={onNotInMvpClick}
					/>
				)
			}
		/>
	);
};

const Address: FC = () => {
	const profileResult = useProfile();
	const adressesResult = useContactAdresses();

	const ai = useAppInsightsContext();

	const handleClick = (changeText: string): void => {
		ai.trackEvent({
			name: `[Change] ${changeText}`,
			properties: {
				type: "not in MVP",
			},
		});
	};

	return (
		<>
			<HeadingM withoutScale sx={{ mb: 0 }}>
				{cs.global.permanentResidence}
			</HeadingM>
			{profileResult.isLoading && <AddressBox isLoading={true} />}
			{profileResult?.data?.permanentAddresses?.map((item, i) => {
				return (
					<Stack spacing={0} key={i}>
						<AddressBox
							key={i}
							address={item}
							label={cs.profile.person.sameForAllAddressesLabel}
						/>
						<Attention severity="info" variant="transparent">
							<span
								dangerouslySetInnerHTML={{
									__html: cs.profile.person.permanentAddressInfo,
								}}
							/>
						</Attention>
					</Stack>
				);
			})}
			<HeadingM withoutScale>{cs.global.contactAddress}</HeadingM>
			{adressesResult.isLoading && (
				<AddressBox
					isLoading
					editAction={
							<Trigger
								sx={{
									ml: { md: "40%", sm: "0" },
								}}
								disabled
								onClick={() => {}}
							>
							{cs.changeAddress.changeContactAddress}
						</Trigger>
					}
				/>
			)}
			{adressesResult.data?.contactAddresses?.map((item, i) => {
				let formattedLabel = `${getContractTypeToTitle(item.type) ?? ""}`;
				item.firstInsuredPerson?.name &&
					(formattedLabel += ` - ${item.firstInsuredPerson.name}`);
				item.firstInsuredPerson?.birthday &&
					(formattedLabel += ` (${dateTimeFormat.format(
						new Date(item.firstInsuredPerson.birthday)
					)})`);

				return (
					<ContactAddressBox
						key={`contactAddresses-${i}`}
						item={item}
						formattedLabel={item && formattedLabel}
						profileLoading={profileResult.isLoading}
						onNotInMvpClick={() =>
							handleClick(cs.profile.person.changeContactAddress)
						}
					/>
				);
			})}
		</>
	);
};

export default withProtection(Address);

export const Head: FC = () => (
	<Seo title={Title}>
		<meta name="robots" content="noindex" />
	</Seo>
);
